import { useEffect, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AuthChangeEvent } from '@supabase/supabase-js';
import { userResponseService } from '@/services/userResponseService';
import { User } from '../types';
import { createUserFromSupabaseUser } from '../utils';
import { recordLoginSession } from '../sessionTracking';

export const useAuthInitialization = () => {
  const queryClient = useQueryClient();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [sessionExpireTime, setSessionExpireTime] = useState<number | null>(null);

  const memoizedSetCurrentUser = useCallback(setCurrentUser, []);
  const memoizedSetSessionExpireTime = useCallback(setSessionExpireTime, []);

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      console.log("Checking for existing session");
      try {
        setAuthLoading(true);
        // Guard against hanging forever when Supabase is unreachable.
        const SESSION_TIMEOUT_MS = 4000;
        const sessionResult = await Promise.race([
          supabase.auth.getSession(),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error(`Supabase session check timed out after ${SESSION_TIMEOUT_MS}ms`)), SESSION_TIMEOUT_MS)
          ),
        ]);

        const { data: { session }, error } = sessionResult;
        
        if (error) {
          console.error("Error checking session:", error);
          return;
        }
        
        if (session?.user) {
          console.log("Found existing session for user:", session.user.id);
          console.log("Session expires at:", new Date(session.expires_at * 1000).toLocaleString());
          
          memoizedSetSessionExpireTime(session.expires_at);
          memoizedSetCurrentUser(createUserFromSupabaseUser(session.user));
        } else {
          console.log("No existing session found");
        }
      } catch (error) {
        console.error("Unexpected error during session check:", error);
      } finally {
        setAuthLoading(false);
        setAuthInitialized(true);
      }
    };
    
    checkSession();
  }, []); // Empty dependency array - only run once on mount

  // Listen for auth changes
  useEffect(() => {
    console.log("Setting up auth state change listener");
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, session) => {
        console.log("Auth state changed:", event, "Session user ID:", session?.user?.id);

        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
          userResponseService.resetAtomicLearnerFieldSavesForAuthChange();
        }

        switch (event) {
          case 'SIGNED_IN':
          case 'TOKEN_REFRESHED':
          case 'USER_UPDATED':
            if (session?.user) {
              console.log(`User event: ${event} for user:`, session.user.id);
              
              memoizedSetCurrentUser(createUserFromSupabaseUser(session.user));
              memoizedSetSessionExpireTime(session.expires_at);

              if (event === 'SIGNED_IN') {
                void recordLoginSession(session.user.id, { source: 'auth_state_change' }).catch((error) => {
                  console.error("Failed to log auth state session:", error);
                });
              }

              // If user was created without a password (workshop sync), redirect to set one
              if (event === 'SIGNED_IN' && session.user.user_metadata?.needs_password) {
                const currentPath = window.location.pathname;
                if (currentPath !== '/set-password') {
                  console.log('[Auth] User needs to set password, redirecting...');
                  window.location.href = '/set-password';
                }
              }
            } else {
              console.warn(`Auth event ${event} received without session.user`);
            }
            break;
          case 'SIGNED_OUT':
            console.log("User signed out");
            memoizedSetCurrentUser(null);
            memoizedSetSessionExpireTime(null);
            // Clear cached query data on every sign-out path (including direct
            // supabase.auth.signOut() calls that bypass useAuthMethods.logout),
            // so the next user on this tab never sees the prior user's data.
            queryClient.clear();
            break;
          case 'INITIAL_SESSION':
            // Skip INITIAL_SESSION as it's already handled by checkSession
            console.log("Skipping INITIAL_SESSION event (already handled)");
            break;
          default:
            console.log("Unhandled or other auth event:", event);
        }
      }
    );
    
    return () => {
      console.log("Cleaning up auth state change listener");
      subscription.unsubscribe();
    };
  }, []); // Empty dependency array - only set up listener once

  return {
    currentUser,
    setCurrentUser: memoizedSetCurrentUser,
    authInitialized,
    authLoading,
    setAuthLoading,
    sessionExpireTime,
    setSessionExpireTime: memoizedSetSessionExpireTime
  };
};
